import type { CurrencyCode, UpsellProductFragment } from '#graphql-operations'

export interface UpsellDisplayItem {
  id: string
  slug?: string
  name: string
  primary: string
  price: string
  /**
   * The same figure unformatted, with the currency that formatted it. A
   * campaign coupon has to be priced from the raw amount, and every rail built
   * on this shape formats `price` here and then has nothing left to discount.
   */
  priceAmount?: number
  currency?: CurrencyCode
  rating: { stars: number, count: number } | null
  variantId?: string
  /**
   * Carried through so a campaign coupon can tell a gift card from
   * merchandise — the rails price with the code deducted, and a gift card is
   * sold at face value. See `couponExcludes` in useUrlCoupon.
   */
  facetValues?: UpsellProductFragment['facetValues']
}

export function upsellFirstInStockVariant(product: UpsellProductFragment) {
  return product.variants.find(variant => variant.stockLevel !== 'OUT_OF_STOCK')
}

export function toUpsellDisplayItem(
  product: UpsellProductFragment,
  formatPrice: (value: number, currency: CurrencyCode) => string,
): UpsellDisplayItem {
  const variant = upsellFirstInStockVariant(product) ?? product.variants[0]
  const rating = product.customFields?.reviewCount
    ? { stars: product.customFields.reviewRating ?? 0, count: product.customFields.reviewCount }
    : null
  return {
    id: String(product.id),
    slug: product.slug,
    name: product.name,
    // The full asset, falling back to the preview: preview sizes are not
    // guaranteed and some products carry one as small as 179x224, which the
    // 168px-wide "complete the look" tile then enlarges.
    primary: product.featuredAsset?.source ?? product.featuredAsset?.preview ?? '',
    price: variant ? formatPrice(variant.price, variant.currency) : '',
    priceAmount: variant?.price,
    currency: variant?.currency,
    rating,
    variantId: variant ? String(variant.id) : undefined,
    facetValues: product.facetValues,
  }
}
